import React from 'react'

import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'

import { CONTENT_MAX_WIDTH, PAGE_GUTTER, PAGE_GUTTER_MOBILE } from 'constants/layout'

export type PageContainerProps = {
  children?: React.ReactNode
  /**
   * Override the `md`-and-up side gutter.
   *
   * Defaults to `PAGE_GUTTER`, which is what every page should take. The ONE
   * exception is the certificates list, which passes
   * `PAGE_GUTTER - CONTENT_INNER_PAD` (8) because its own children already supply
   * the other 16: `HeaderBar` pads itself 16px and the grid wrapper is `p-4`. That
   * shape is the only reason to pass this; anywhere else a custom gutter is the
   * drift this component exists to stop.
   *
   * There is deliberately NO `maxWidth` prop. There used to be one, for choosing
   * between an index width and a reading width, and it had exactly one call site by
   * the time the two collapsed into `CONTENT_MAX_WIDTH` — so a prop that no page
   * passes is a per-page width decision left lying around for someone to pick up.
   * If a page ever genuinely needs a different cap, that is a change to the rule in
   * `constants/layout`, argued there, not a prop threaded through here.
   */
  gutter?: number
  /**
   * Override the `xs` side gutter. Same rule as `gutter`: the certificates list
   * passes 0 because its `HeaderBar` and grid wrapper supply the phone's whole
   * 16px themselves. Anywhere else, leave it.
   *
   * Separate prop rather than `sx={{ px: { xs: 0 } }}` because an `sx` object
   * REPLACES a responsive key wholesale — that spelling silently drops the `md`
   * gutter and the column loses its desktop padding.
   */
  gutterMobile?: number
  /** Overrides merged AFTER the container's own rules — padding, flex, min-height. */
  sx?: SxProps<Theme>
  className?: string
}

/**
 * The app's content column: centred, capped, guttered.
 *
 * Every page's content goes in one of these, and they are all the same width —
 * `CONTENT_MAX_WIDTH` with a `PAGE_GUTTER` a side. The reasoning, including the
 * two-width rule this replaced and what measuring it showed, is written out in full
 * at the top of `constants/layout`. Read it before adding a page; the failure this
 * component exists to prevent is four different content widths across the app, each
 * invented locally because the number lived inside a page instead of a constant.
 *
 * ## It is a flex column, not a bare Box
 *
 * `layouts/Default` already records why: the page used to be a direct flex child
 * of the `main` Stack, and inserting a NON-flex node into that chain silently
 * broke the `flex: 1 1 auto` run that pushes a footer to the bottom — the Profile
 * page stopped stretching and the fixed bottom bar positioned against a box taller
 * than the viewport. So this is `display: flex; flex-direction: column` with
 * `minWidth: 0`, which is what the Stacks it replaces already were, and it is
 * layout-neutral by construction.
 *
 * ## Mobile
 *
 * `PAGE_GUTTER_MOBILE` at `xs`. Every page this replaced was already on 16px
 * there, so adopting it is a desktop-only change — deliberately, because the phone
 * is the surface this app is used on and it was not the thing that had drifted.
 *
 * ## What it does NOT do
 *
 * It does not set vertical padding, a background, or a min-height. Those differ
 * per page for real reasons (the scans map is exactly one viewport tall and runs
 * to the bottom edge; a list grows past the fold), so they stay at the call site
 * via `sx`.
 */
export const PageContainer: React.FC<PageContainerProps> = ({
  children,
  gutter = PAGE_GUTTER,
  gutterMobile = PAGE_GUTTER_MOBILE,
  sx,
  className,
}) => (
  <Box
    className={className}
    sx={[
      {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        // The standard flexbox escape from `min-width: auto`, so a long unbroken
        // string inside cannot push the column past its cap.
        minWidth: 0,
        maxWidth: `${CONTENT_MAX_WIDTH}px`,
        mx: 'auto',
        px: { xs: `${gutterMobile}px`, md: `${gutter}px` },
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    {children}
  </Box>
)
